import { useMemo, type FC } from "react";

import {
  CloseOutlined,
  EditOutlined,
  PushpinFilled,
  PushpinOutlined,
} from "@ant-design/icons";
import { Avatar, Skeleton } from "antd";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../../../utils/dateService";

import type { Note } from "../../../../../queries/user-notes/types";

export type { Note };

export interface NotesProps {
  notes: Note[];
  profilePhoto?: string;
  isLoading?: boolean;
  isError?: boolean;
  pendingNoteId?: string | null;
  onAddNote: () => void;
  onEditNote: (note: Note) => void;
  onDeleteNote: (noteId: Note["note_id"]) => void;
  onPinNote: (noteId: Note["note_id"]) => void;
  onUnpinNote: (noteId: Note["note_id"]) => void;
  onRetry?: () => void;
}

const TIME_FORMAT = "MMM DD, YYYY hh:mm A";

// BE sends `created_at` with an explicit offset (e.g. `…+03:00`) — convert
// the absolute instant into the admin's configured timezone for display.
const formatNoteTime = (iso: string | null | undefined) =>
  iso ? convertToUserTimezone(iso, TIME_FORMAT) : "";

const Notes: FC<NotesProps> = ({
  notes,
  profilePhoto,
  isLoading = false,
  isError = false,
  pendingNoteId = null,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onPinNote,
  onUnpinNote,
  onRetry,
}) => {
  // Pinned notes always render at the top. Array.prototype.sort is stable in
  // modern engines, so notes within each group keep their insertion order.
  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) => Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned)),
      ),
    [notes],
  );

  return (
    <>
      <div className="notes-header">
        <h2>{AppStrings.NOTES}</h2>
        <button onClick={onAddNote} disabled={isLoading || isError}>
          {AppStrings.ADD_NOTE}
        </button>
      </div>
      <div className="notes-container" data-testid="notes-container">
        {isLoading && (
          <div data-testid="notes-loading" className="notes-loading">
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
          </div>
        )}

        {!isLoading && isError && (
          <div data-testid="notes-error" className="notes-error">
            <p>{AppStrings.NOTES_LOAD_ERROR}</p>
            {onRetry && (
              <button type="button" onClick={onRetry}>
                {AppStrings.RETRY}
              </button>
            )}
          </div>
        )}

        {!isLoading && !isError && sortedNotes.length === 0 && (
          <p data-testid="notes-empty" className="notes-empty">
            {AppStrings.NO_NOTES_YET}
          </p>
        )}

        {!isLoading &&
          !isError &&
          sortedNotes.map((note) => {
            const isPinned = Boolean(note.is_pinned);
            const PinIcon = isPinned ? PushpinFilled : PushpinOutlined;
            // Permission rules (author-only edit/unpin, super-admin delete,
            // etc.) are enforced by the backend. We only gate clicks while a
            // mutation for this note is in flight; the BE returns 403 with a
            // toastable message if the caller is not allowed.
            const displayName = note.author_agent_email;
            const wasEdited = Boolean(note.updated_at);
            const isPending = pendingNoteId === note.note_id;

            const handlePinClick = () => {
              if (isPending) return;
              if (isPinned) {
                onUnpinNote(note.note_id);
                return;
              }
              onPinNote(note.note_id);
            };

            return (
              <div
                key={note.note_id}
                data-testid={`note-${note.note_id}`}
                className={`note${isPinned ? " pinned" : ""}${
                  isPending ? " note-pending" : ""
                }`}
                data-pinned={isPinned}
                aria-busy={isPending}
              >
                <div className="note-header">
                  <div className="note-header-left">
                    <Avatar
                      size={28}
                      src={profilePhoto}
                      style={{
                        backgroundColor: "var(--COLOR-PRIMARY)",
                        flexShrink: 0,
                        fontSize: 14,
                      }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <p className="note-name">{displayName}</p>
                  </div>
                  <div className="note-header-right">
                    <p className="note-time">
                      {formatNoteTime(note.created_at)}
                      {wasEdited && (
                        <span className="note-edited">
                          {" "}
                          ({AppStrings.EDITED})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="note-body">
                  <p className="note-text">{note.body}</p>
                  <div className="note-actions">
                    <button
                      type="button"
                      className="note-action-button"
                      aria-label={isPinned ? "unpin note" : "pin note"}
                      disabled={isPending}
                      onClick={handlePinClick}
                    >
                      <PinIcon className="pin-icon" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="note-action-button"
                      aria-label="Edit note"
                      disabled={isPending}
                      onClick={() => onEditNote(note)}
                    >
                      <EditOutlined className="edit-icon" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="note-action-button"
                      aria-label="Delete note"
                      disabled={isPending}
                      onClick={() => onDeleteNote(note.note_id)}
                    >
                      <CloseOutlined className="close-icon" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default Notes;
